import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { getCurrentFinancialYear } from "../../services/auth_service";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import {
  createDriver,
  getDriverById,
  updateDriver,
  getLocations,
  getBranches,
  getStatuses,
  uploadDriverDocument,
  getFinancialYears,
  fetchDriverFile,
  extractFileName,
} from "../../services/driver_service";
import { getSupplierTypes } from "../../services/supplier_service";
import { isSuperadmin, hasPermission } from "../utils/permissions";

import "./addDriver.css";

const INITIAL_FORM = {
  location_id: "",
  branch_id: "",
  full_name: "",
  contact_number: "",
  email: "",
  blood_group: "",
  date_of_birth: "",
  aadhaar_number: "",
  adhaar_url: "",
  permanent_address: "",
  temporary_address: "",
  city: "",
  state: "",
  pincode: "",
  license_number: "",
  license_expiry: "",
  license_file_url: "",
  pancard_number: "",
  pancard_file_url: "",
  bank_passbook_photo_url: "",
  gas_bill_photo_url: "",
  electricity_bill_photo_url: "",
  reference_person_name: "",
  reference_contact_number_1: "",
  reference_contact_number_2: "",
  driver_photo_url: "",
  date_of_joining: "",
  emergency_contact_name: "",
  emergency_contact_number: "",
  character_nature: "",
  status_id: "",
  supplier_type_id: "",
  financial_year_id: null,
};

const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const CHARACTER_NATURE_OPTIONS = ["Excellent", "Very Good", "Good", "Fair", "Poor"];
const DATE_FIELDS = ["date_of_birth", "license_expiry", "date_of_joining"];
const INT_FIELDS = ["location_id", "branch_id", "status_id", "supplier_type_id"];
const formatAlphaUpper = (v) => v.replace(/[^A-Za-z\s]/g, "").toUpperCase();
const formatDigits = (v, max) => v.replace(/\D/g, "").slice(0, max);
const formatAlnumUpper = (v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
const formatAddressUpper = (v) => v.replace(/[^A-Za-z0-9,.\-/\s]/g, "").toUpperCase();
const formatEmailLower = (v) => v.toLowerCase();
const TODAY_STR = new Date().toISOString().split("T")[0];
const MIN_DATE_STR = "1900-01-01";
const MAX_DATE_STR = `${new Date().getFullYear() + 50}-12-31`;
const isValidDateStr = (v) => {
  if (!v) return true;
  const yearPart = v.split("-")[0];
  if (yearPart.length !== 4) return false;
  const d = new Date(v);
  return !isNaN(d.getTime());
};

const MOBILE_REGEX = /^[6-9]\d{9}$/;

const BACKEND_FIELD_ALIASES = {
  mobile: "contact_number",
  contact_number: "contact_number",
  phone: "contact_number",
  aadhaar_number: "aadhaar_number",
  aadhar_number: "aadhaar_number",
  license_number: "license_number",
  driving_license_number: "license_number",
  pancard_number: "pancard_number",
  pan_number: "pancard_number",
  email: "email",
};

const FIELD_LABELS = {
  contact_number: "mobile number",
  aadhaar_number: "Aadhaar number",
  license_number: "driving license number",
  pancard_number: "PAN number",
  email: "email address",
  reference_contact_number_1: "reference contact number",
};

const DUPLICATE_STRING_FIELD_MATCHERS = [
  { pattern: /mobile/i, field: "contact_number" },
  { pattern: /aadhaar|aadhar/i, field: "aadhaar_number" },
  { pattern: /license/i, field: "license_number" },
  { pattern: /pan\s?card|\bpan\b/i, field: "pancard_number" },
  { pattern: /email/i, field: "email" },
  { pattern: /reference.*contact.*number/i, field: "reference_contact_number_1" },
];

const buildDuplicateFieldMessage = (field) => {
  const label = FIELD_LABELS[field] || "value";
  return `This ${label} is already registered to another driver. Please use a different one.`;
};

const extractBackendFieldErrors = (detail) => {
  const fieldErrors = {};

  if (Array.isArray(detail)) {
    detail.forEach((d) => {
      const rawField = d.loc?.[d.loc.length - 1];
      const mappedField = BACKEND_FIELD_ALIASES[rawField];
      if (mappedField && d.msg) {
        fieldErrors[mappedField] = d.msg;
      }
    });
    return fieldErrors;
  }

  if (typeof detail === "string" && detail.trim()) {
    const match = DUPLICATE_STRING_FIELD_MATCHERS.find(({ pattern }) => pattern.test(detail));
    if (match) {
      fieldErrors[match.field] = buildDuplicateFieldMessage(match.field);
    }
  }

  return fieldErrors;
};

const buildFriendlyErrorMessage = (err, isEdit) => {
  const action = isEdit ? "update" : "save";

  if (!err?.response) {
    return `We couldn't reach the server to ${action} this driver. Please check your internet connection and try again in a moment.`;
  }

  const status = err.response.status;
  const detail = err.response.data?.detail;

  if (Array.isArray(detail)) {
    return `Some of the details entered aren't valid. Please review the highlighted fields and try again.`;
  }

  if (status >= 500) {
    return `Something went wrong on our server while trying to ${action} this driver. This isn't caused by anything you entered — please try again shortly, and let support know if it keeps happening.`;
  }

  if (status === 404) {
    return `This driver record could not be found. It may have been removed by someone else — please refresh the drivers list.`;
  }

  if (status === 401 || status === 403) {
    return `You don't have permission to ${action} this driver, or your session has expired. Please log in again.`;
  }

  if (status === 400 || status === 409 || status === 422) {
    return `Some of the details entered conflict with an existing record or aren't valid. Please review the form and try again.`;
  }

  return `We couldn't ${action} this driver. Please review the form and try again.`;
};

const deriveIsActiveFromStatusId = (statusId, statusList) => {
  if (!statusId) return true;
  const status = statusList.find((s) => String(s.id) === String(statusId));
  if (!status) return true;
  const name = (status.name || status.status_name || "").toLowerCase();
  return name.includes("active") && !name.includes("inactive") && !name.includes("deactive");
};

const sanitizePayload = (form) => {
  const payload = { ...form };
  delete payload.driver_code;
  DATE_FIELDS.forEach((key) => { payload[key] = payload[key] || null; });
  INT_FIELDS.forEach((key) => { payload[key] = Number(payload[key]) || null; });
  Object.keys(payload).forEach((key) => { if (payload[key] === "") payload[key] = null; });
  return payload;
};

const FORM_TO_BACKEND_FIELD = {
  contact_number: "mobile",
  date_of_joining: "joining_date",
};

const NON_PAYLOAD_FIELDS = new Set(["driver_code"]);

const buildUpdatePayload = (form, original) => {
  if (!original) {
    return sanitizePayload(form);
  }

  const diff = {};

  Object.keys(form).forEach((key) => {
    if (NON_PAYLOAD_FIELDS.has(key)) return;

    const backendKey = FORM_TO_BACKEND_FIELD[key] || key;
    const originalValue = original[backendKey];

    let formValue = form[key];
    if (DATE_FIELDS.includes(key)) formValue = formValue || null;
    if (INT_FIELDS.includes(key)) formValue = Number(formValue) || null;
    if (formValue === "") formValue = null;

    const normalisedOriginal = originalValue === "" ? null : originalValue;

    const changed = INT_FIELDS.includes(key)
      ? Number(formValue) !== Number(normalisedOriginal) &&
      !(formValue == null && normalisedOriginal == null)
      : String(formValue ?? "") !== String(normalisedOriginal ?? "");

    if (changed) {
      diff[backendKey] = formValue;
    }
  });

  return diff;
};

const handleViewFile = async (fileMeta) => {
  if (!fileMeta?.url) return;

  if (fileMeta.isBlob) {
    window.open(fileMeta.url, "_blank", "noopener,noreferrer");
    return;
  }

  try {
    const blob = await fetchDriverFile(fileMeta.url);
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    console.error("View failed:", err);
    alert("Couldn't open this file. Please try again.");
  }
};

const handleDownloadFile = async (fileMeta) => {
  if (!fileMeta?.url) return;

  try {
    let blob;
    if (fileMeta.isBlob) {
      const res = await fetch(fileMeta.url);
      blob = await res.blob();
    } else {
      blob = await fetchDriverFile(fileMeta.url);
    }
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileMeta.name || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed:", err);
    alert("Couldn't download this file. Please try again.");
  }
};

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export default function AddDriver() {
  const navigate = useNavigate();
  const { id } = useParams();
  const confirm = useConfirm();
  const superadminUser = isSuperadmin();
  const canEdit = superadminUser || hasPermission("drivers", "edit");
  const readOnly = Boolean(id) && !canEdit;
  const mastersLoaded = useRef(false);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [driverCode, setDriverCode] = useState("");
  const [locationLocked, setLocationLocked] = useState(false);
  const [originalDriver, setOriginalDriver] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [currentFY, setCurrentFY] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFields, setUploadingFields] = useState({});
  const [filePreviews, setFilePreviews] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({});

  useEffect(() => {
    if (!mastersLoaded.current) {
      mastersLoaded.current = true;
      loadMasters();
    }
    if (id) loadDriver();
  }, [id]);

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;
    const raf = requestAnimationFrame(() => {
      const firstErrorEl = document.querySelector(".field-error");
      const target = firstErrorEl?.closest(".form-field") || firstErrorEl;
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = target.querySelector?.("input, select, textarea");
        if (input) input.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [errors]);

  useEffect(() => {
    if (!submitError) return;
    const raf = requestAnimationFrame(() => {
      const banner = document.querySelector(".submit-error-banner");
      if (banner) {
        banner.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [submitError]);

  useEffect(() => {
    if (!statusMessage) return;
    const raf = requestAnimationFrame(() => {
      const banner = document.querySelector(
        statusMessage.type === "success" ? ".submit-success-banner" : ".submit-warning-banner"
      );
      if (banner) {
        banner.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [statusMessage]);

  const loadMasters = async () => {
    const [locationData, branchData, statusData, supplierTypeData, fyListData, currentFyData] =
      await Promise.allSettled([
        getLocations(),
        getBranches(),
        getStatuses(),
        getSupplierTypes(),
        getFinancialYears(),
        getCurrentFinancialYear(),
      ]);

    if (locationData.status === "fulfilled") {
      const locs = locationData.value || [];
      setLocations(locs);

      if (!id && !canEdit && locs.length === 1) {
        setLocationLocked(true);
        setForm((f) => ({ ...f, location_id: String(locs[0].id) }));
      }
    }

    if (branchData.status === "fulfilled") setBranches(branchData.value);
    if (statusData.status === "fulfilled") setStatuses(statusData.value);
    if (supplierTypeData.status === "fulfilled") setSupplierTypes(supplierTypeData.value || []);
    if (fyListData.status === "fulfilled") setFinancialYears(fyListData.value || []);
    if (currentFyData.status === "fulfilled") setCurrentFY(currentFyData.value);
  };

  const loadDriver = async () => {
    try {
      const data = await getDriverById(id);
      setDriverCode(data.driver_code || "");
      setOriginalDriver(data);
      setForm({
        ...INITIAL_FORM,
        ...data,
        location_id: String(data.location_id ?? ""),
        branch_id: String(data.branch_id ?? ""),
        status_id: String(data.status_id ?? ""),
        supplier_type_id: String(data.supplier_type_id ?? ""),
        date_of_birth: data.date_of_birth || "",
        license_expiry: data.license_expiry || "",
        date_of_joining: data.joining_date || data.date_of_joining || "",
        contact_number: data.contact_number || data.mobile || "",
        adhaar_url: data.adhaar_url || "",
        license_file_url: data.license_file_url || "",
        pancard_file_url: data.pancard_file_url || "",
        bank_passbook_photo_url: data.bank_passbook_photo_url || "",
        gas_bill_photo_url: data.gas_bill_photo_url || "",
        electricity_bill_photo_url: data.electricity_bill_photo_url || "",
        driver_photo_url: data.driver_photo_url || "",
        financial_year_id: data.financial_year_id ?? null,
      });

      const existingPreviews = {};
      [
        "adhaar_url", "license_file_url", "pancard_file_url",
        "bank_passbook_photo_url", "gas_bill_photo_url",
        "electricity_bill_photo_url", "driver_photo_url",
      ].forEach((key) => { if (data[key]) existingPreviews[key] = data[key]; });
      setFilePreviews(existingPreviews);
    } catch (err) {
      console.error("Load driver error:", err);
    }
  };

  const field = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const handleFileChange = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type.startsWith("image/")) {
      setFilePreviews((p) => ({
        ...p, [key]: { url: URL.createObjectURL(file), name: file.name, isImage: true },
      }));
    } else {
      setFilePreviews((p) => ({
        ...p, [key]: { url: null, name: file.name, isImage: false },
      }));
    }

    setPendingFiles((p) => ({ ...p, [key]: { file } }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.location_id)
      newErrors.location_id = "State Name is required.";
    if (!form.branch_id)
      newErrors.branch_id = "Branch Name is required.";
    if (!form.supplier_type_id)
      newErrors.supplier_type_id = "Driver Type is required.";
    if (!form.full_name)
      newErrors.full_name = "Full Name is required.";

    if (!form.contact_number)
      newErrors.contact_number = "Contact Number is required.";
    else if (!MOBILE_REGEX.test(form.contact_number))
      newErrors.contact_number = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Enter a valid email address.";

    if (!form.date_of_birth) {
      newErrors.date_of_birth = "Date of Birth is required.";
    } else if (!isValidDateStr(form.date_of_birth)) {
      newErrors.date_of_birth = "Enter a valid Date of Birth.";
    } else if (new Date(form.date_of_birth) > new Date()) {
      newErrors.date_of_birth = "Date of Birth cannot be in the future.";
    }

    if (!form.aadhaar_number)
      newErrors.aadhaar_number = "Aadhar Number is required.";
    else if (!/^\d{12}$/.test(form.aadhaar_number))
      newErrors.aadhaar_number = "Aadhar Number must be exactly 12 digits.";
    if (!form.permanent_address)
      newErrors.permanent_address = "Permanent Address is required.";
    if (!form.temporary_address)
      newErrors.temporary_address = "Temporary Address is required.";
    if (!form.city)
      newErrors.city = "City Name is required.";
    if (!form.state)
      newErrors.state = "State Name is required.";
    if (!form.pincode)
      newErrors.pincode = "Pincode is required.";
    else if (!/^\d{6}$/.test(form.pincode))
      newErrors.pincode = "Pincode must be exactly 6 digits.";
    if (!form.license_number)
      newErrors.license_number = "Driving License Number is required.";

    if (!form.license_expiry) {
      newErrors.license_expiry = "D.L Expiry Date is required.";
    } else if (!isValidDateStr(form.license_expiry)) {
      newErrors.license_expiry = "Enter a valid D.L Expiry Date.";
    }

    if (!form.pancard_number)
      newErrors.pancard_number = "Pancard Number is required.";
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pancard_number))
      newErrors.pancard_number = "Must be a valid 10-character PAN (e.g. ABCDE1234F).";
    if (!form.reference_person_name)
      newErrors.reference_person_name = "Reference Contact Name is required.";

    if (form.reference_contact_number_1 && !MOBILE_REGEX.test(form.reference_contact_number_1))
      newErrors.reference_contact_number_1 = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";
    if (form.reference_contact_number_2 && !MOBILE_REGEX.test(form.reference_contact_number_2))
      newErrors.reference_contact_number_2 = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";

    if (!form.date_of_joining) {
      newErrors.date_of_joining = "Date of Joining is required.";
    } else if (!isValidDateStr(form.date_of_joining)) {
      newErrors.date_of_joining = "Enter a valid Date of Joining.";
    } else if (new Date(form.date_of_joining) > new Date()) {
      newErrors.date_of_joining = "Date of Joining cannot be in the future.";
    }

    if (!form.emergency_contact_number)
      newErrors.emergency_contact_number = "Emergency Contact Number is required.";
    else if (!MOBILE_REGEX.test(form.emergency_contact_number))
      newErrors.emergency_contact_number = "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";

    if (!form.character_nature)
      newErrors.character_nature = "Character Nature is required.";
    if (!form.status_id)
      newErrors.status_id = "Status is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = async () => {
    if (readOnly) {
      navigate("/drivers");
      return;
    }
    const confirmed = await confirm({
      title: "Unsaved Changes",
      message: "Are you sure you want to go back? Any unsaved changes will be lost.",
      confirmLabel: "Go Back",
      cancelLabel: "Stay on Page",
      danger: true,

    });
    if (confirmed) {
      navigate("/drivers");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly || !canEdit) return;

    setSubmitError(null);
    setStatusMessage(null);

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = id
        ? buildUpdatePayload(form, originalDriver)
        : {
          ...sanitizePayload(form),
          mobile: form.contact_number,
          joining_date: form.date_of_joining,
        };

      if (!id) delete payload.financial_year_id;

      const derivedIsActive = deriveIsActiveFromStatusId(
        form.status_id,
        statuses
      );

      let driverId = id;

      if (id) {
        const originalIsActive = Boolean(originalDriver?.is_active);

        if (originalIsActive !== derivedIsActive) {
          payload.is_active = derivedIsActive;
        }

        if (Object.keys(payload).length === 0) {
          setStatusMessage({
            type: "success",
            text: "No changes were made."
          });

          setTimeout(() => navigate("/drivers"), 1000);

          setSubmitting(false);
          return;
        }

        await updateDriver(id, payload);
      } else {
        payload.is_active = derivedIsActive;
        const created = await createDriver(payload);
        driverId = created?.id;
        if (!driverId) {
          throw new Error("Driver was created but no id was returned by the server.");
        }
      }

      const pendingKeys = Object.keys(pendingFiles);
      if (pendingKeys.length) {
        const busyFlags = {};
        pendingKeys.forEach((k) => { busyFlags[k] = true; });
        setUploadingFields((u) => ({ ...u, ...busyFlags }));

        const failedKeys = [];
        for (const key of pendingKeys) {
          const { file } = pendingFiles[key];
          try {
            await uploadDriverDocument(driverId, key, file);
          } catch (err) {
            console.error(`File upload failed for ${key}:`, err);
            failedKeys.push(key);
          } finally {
            setUploadingFields((u) => ({ ...u, [key]: false }));
          }
        }

        if (failedKeys.length) {
          setStatusMessage({
            type: "warning",
            text:
              `Driver ${id ? "updated" : "created"} successfully, but ` +
              `${failedKeys.length === 1 ? "this document" : "these documents"} ` +
              `couldn't be uploaded: ${failedKeys.join(", ")}. ` +
              `You can open the driver in Edit mode afterwards to upload ${failedKeys.length === 1 ? "it" : "them"} again.`,
          });
          setTimeout(() => navigate("/drivers"), 2600);
          return;
        }
      }

      setStatusMessage({
        type: "success",
        text: id ? "Driver updated successfully." : "Driver created successfully.",
      });
      setTimeout(() => navigate("/drivers"), 1400);
    } catch (err) {
      console.error("Driver save/update error:", err);
      console.error("Backend response:", err?.response?.data);

      const detail = err?.response?.data?.detail;
      const backendFieldErrors = extractBackendFieldErrors(detail);

      if (Object.keys(backendFieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...backendFieldErrors }));
      }

      if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => item?.msg || item?.message)
          .filter(Boolean);

        setSubmitError(
          messages.length > 0
            ? messages.join(" ")
            : "Please review the highlighted fields and try again."
        );
      } else if (typeof detail === "string" && detail.trim()) {
        setSubmitError(detail);
      } else if (!err?.response) {
        setSubmitError(buildFriendlyErrorMessage(err, id));
      } else {
        setSubmitError(buildFriendlyErrorMessage(err, id));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const PHOTO_ONLY_FIELDS = new Set([
    "bank_passbook_photo_url",
    "gas_bill_photo_url",
    "electricity_bill_photo_url",
    "driver_photo_url",
  ]);

  const renderFileField = (key, label) => {
    const preview = filePreviews[key];
    const isUploading = uploadingFields[key];
    const accept = PHOTO_ONLY_FIELDS.has(key) ? "image/*" : "image/*,.pdf";

    let resolved = null;
    if (preview && typeof preview === "object") {
      resolved = { url: preview.url, name: preview.name, isImage: preview.isImage, isBlob: true };
    } else if (preview && typeof preview === "string") {
      const filename = extractFileName(preview);
      resolved = { url: filename, name: filename, isImage: false, isBlob: false };
    }

    return (
      <div className="form-field file-field">
        <label className="field-label">{label}</label>
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange(key)}
          disabled={isUploading}
        />
        {isUploading && (
          <div className="file-uploading"><div className="spinner" /> Uploading…</div>
        )}

        {!isUploading && resolved && (
          <div className="file-preview-block">
            {resolved.isImage && resolved.url && (
              <img src={resolved.url} alt={`${label} Preview`} className="photo-preview" />
            )}
            {(!resolved.isImage || !resolved.url) && (
              <span className="file-name-preview">📄 {resolved.name}</span>
            )}

            {resolved.url && (
              <div className="file-actions">
                <a
                  href="#"
                  className="file-action-btn file-view-btn"
                  title="View"
                  onClick={(e) => { e.preventDefault(); handleViewFile(resolved); }}
                >
                  <EyeIcon />
                  <span>View</span>
                </a>
                <a
                  href="#"
                  className="file-action-btn file-download-btn"
                  title="Download"
                  onClick={(e) => { e.preventDefault(); handleDownloadFile(resolved); }}
                >
                  <DownloadIcon />
                  <span>Download</span>
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />
        <div className="add-driver-page">
          <div className="driver-card">

            <div className="driver-card-header">

              <div className="driver-card-heading">
                <h2>{id ? (readOnly ? "View Driver" : "Edit Driver") : "Add Driver"}</h2>
                <span className="driver-card-subtitle">
                  {readOnly
                    ? "Viewing driver profile (read-only)"
                    : id
                      ? "Update driver profile and documents"
                      : "Fill in driver details in the order below"}
                </span>
              </div>
              {id && driverCode && (
                <span className="driver-code-badge" title="Auto-generated driver code">
                  {driverCode}
                </span>
              )}
            </div>

            <form className="driver-form" onSubmit={handleSubmit} noValidate>
              {submitError && (
                <div className="submit-error-banner" role="alert">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="13" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{submitError}</span>
                  <button type="button" className="submit-error-dismiss"
                    onClick={() => setSubmitError(null)} aria-label="Dismiss">
                    ×
                  </button>
                </div>
              )}

              {statusMessage && (
                <div
                  className={statusMessage.type === "success" ? "submit-success-banner" : "submit-warning-banner"}
                  role="status"
                >
                  {statusMessage.type === "success" ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                    </svg>
                  )}
                  <span>{statusMessage.text}</span>
                </div>
              )}

              <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0 }}>

                <div className="form-section-title">
                  <span className="section-num">01</span>Location & Branch
                </div>

                <div className="form-field">
                  <select value={form.location_id}
                    onChange={(e) => field("location_id", e.target.value)}
                    disabled={locationLocked}
                    className={errors.location_id ? "input-error" : ""}>
                    <option value="">Select Location  *</option>
                    {locations.map((x) => (
                      <option key={x.id} value={x.id}>{x.location_name}</option>
                    ))}
                  </select>
                  {locationLocked && (
                    <span className="field-hint">Auto-assigned to your location.</span>
                  )}
                  {errors.location_id && <span className="field-error">{errors.location_id}</span>}
                </div>

                <div className="form-field">
                  <select value={form.branch_id}
                    onChange={(e) => field("branch_id", e.target.value)}
                    className={errors.branch_id ? "input-error" : ""}>
                    <option value="">Select Branch Name *</option>
                    {branches.map((x) => (
                      <option key={x.id} value={x.id}>{x.branch_name}</option>
                    ))}
                  </select>
                  {errors.branch_id && <span className="field-error">{errors.branch_id}</span>}
                </div>
                <div className="form-section-title">
                  <span className="section-num">02</span>Driver Personal Information
                </div>

                <div className="form-field">
                  <select value={form.supplier_type_id}
                    onChange={(e) => field("supplier_type_id", e.target.value)}
                    className={errors.supplier_type_id ? "input-error" : ""}>
                    <option value="">-- Select Driver Type * --</option>
                    {supplierTypes
                      .filter((t) => String(t.id) === "1")
                      .map((t) => (
                        <option key={t.id} value={t.id}>{t.supplier_type_name}</option>
                      ))}
                  </select>
                  {errors.supplier_type_id && <span className="field-error">{errors.supplier_type_id}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Full Name *"
                    value={form.full_name}
                    className={errors.full_name ? "input-error" : ""}
                    onChange={(e) => field("full_name", formatAlphaUpper(e.target.value))} />
                  {errors.full_name && <span className="field-error">{errors.full_name}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Contact Number * (starts with 6-9)"
                    value={form.contact_number} maxLength={10}
                    className={errors.contact_number ? "input-error" : ""}
                    onChange={(e) => field("contact_number", formatDigits(e.target.value, 10))} />
                  {errors.contact_number && <span className="field-error">{errors.contact_number}</span>}
                </div>

                <div className="form-field">
                  <input type="email" placeholder="Email Address"
                    value={form.email}
                    className={errors.email ? "input-error" : ""}
                    onChange={(e) => field("email", formatEmailLower(e.target.value))} />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <div className="form-field">
                  <select value={form.blood_group}
                    onChange={(e) => field("blood_group", e.target.value)}>
                    <option value="">Select Blood Group</option>
                    {BLOOD_GROUP_OPTIONS.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label className="field-label">Date of Birth <span className="req">*</span></label>
                  <input type="date" value={form.date_of_birth}
                    min={MIN_DATE_STR}
                    max={TODAY_STR}
                    className={errors.date_of_birth ? "input-error" : ""}
                    onChange={(e) => field("date_of_birth", e.target.value)} />
                  {errors.date_of_birth && <span className="field-error">{errors.date_of_birth}</span>}
                </div>

                <div className="form-section-title">
                  <span className="section-num">03</span>Aadhar Details
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Aadhar Number *"
                    value={form.aadhaar_number} maxLength={12}
                    className={errors.aadhaar_number ? "input-error" : ""}
                    onChange={(e) => field("aadhaar_number", formatDigits(e.target.value, 12))} />
                  {errors.aadhaar_number && <span className="field-error">{errors.aadhaar_number}</span>}
                </div>

                {renderFileField("adhaar_url", "Aadhar File Upload")}
                <div className="form-section-title">
                  <span className="section-num">04</span>Address
                </div>

                <div className="form-field full-width">
                  <textarea placeholder="Permanent Address *"
                    value={form.permanent_address}
                    className={errors.permanent_address ? "input-error" : ""}
                    onChange={(e) => field("permanent_address", formatAddressUpper(e.target.value))} />
                  {errors.permanent_address && <span className="field-error">{errors.permanent_address}</span>}
                </div>

                <div className="form-field full-width">
                  <textarea placeholder="Temporary Address *"
                    value={form.temporary_address}
                    className={errors.temporary_address ? "input-error" : ""}
                    onChange={(e) => field("temporary_address", formatAddressUpper(e.target.value))} />
                  {errors.temporary_address && <span className="field-error">{errors.temporary_address}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="City *"
                    value={form.city}
                    className={errors.city ? "input-error" : ""}
                    onChange={(e) => field("city", formatAlphaUpper(e.target.value))} />
                  {errors.city && <span className="field-error">{errors.city}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="State *"
                    value={form.state}
                    className={errors.state ? "input-error" : ""}
                    onChange={(e) => field("state", formatAlphaUpper(e.target.value))} />
                  {errors.state && <span className="field-error">{errors.state}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Pincode *"
                    value={form.pincode} maxLength={6}
                    className={errors.pincode ? "input-error" : ""}
                    onChange={(e) => field("pincode", formatDigits(e.target.value, 6))} />
                  {errors.pincode && <span className="field-error">{errors.pincode}</span>}
                </div>
                <div className="form-section-title">
                  <span className="section-num">05</span>Driving License
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Driving License Number *"
                    value={form.license_number}
                    className={errors.license_number ? "input-error" : ""}
                    onChange={(e) => field("license_number", formatAlnumUpper(e.target.value))} />
                  {errors.license_number && <span className="field-error">{errors.license_number}</span>}
                </div>

                <div className="form-field">
                  <label className="field-label">D. L. Expiry Date <span className="req">*</span></label>
                  <input type="date" value={form.license_expiry}
                    min={MIN_DATE_STR}
                    max={MAX_DATE_STR}
                    className={errors.license_expiry ? "input-error" : ""}
                    onChange={(e) => field("license_expiry", e.target.value)} />
                  {errors.license_expiry && <span className="field-error">{errors.license_expiry}</span>}
                </div>

                {renderFileField("license_file_url", "Driving License File Upload")}
                <div className="form-section-title">
                  <span className="section-num">06</span>Pancard
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Pancard Number *"
                    value={form.pancard_number} maxLength={10}
                    className={errors.pancard_number ? "input-error" : ""}
                    onChange={(e) => field("pancard_number", formatAlnumUpper(e.target.value))} />
                  {errors.pancard_number && <span className="field-error">{errors.pancard_number}</span>}
                </div>

                {renderFileField("pancard_file_url", "Pancard File Upload")}
                <div className="form-section-title">
                  <span className="section-num">07</span>Bank & Utility Bills
                </div>

                {renderFileField("bank_passbook_photo_url", "Bank Passbook Photo Upload")}
                {renderFileField("gas_bill_photo_url", "Gas Bill Photo Upload")}
                {renderFileField("electricity_bill_photo_url", "Electricity Bill Photo Upload")}
                <div className="form-section-title">
                  <span className="section-num">08</span>Reference Details
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Reference Person Name *"
                    value={form.reference_person_name}
                    className={errors.reference_person_name ? "input-error" : ""}
                    onChange={(e) => field("reference_person_name", formatAlphaUpper(e.target.value))} />
                  {errors.reference_person_name && <span className="field-error">{errors.reference_person_name}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Reference Contact Number 1 (starts with 6-9)"
                    value={form.reference_contact_number_1} maxLength={10}
                    className={errors.reference_contact_number_1 ? "input-error" : ""}
                    onChange={(e) => field("reference_contact_number_1", formatDigits(e.target.value, 10))} />
                  {errors.reference_contact_number_1 && <span className="field-error">{errors.reference_contact_number_1}</span>}
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Reference Contact Number 2 (starts with 6-9)"
                    value={form.reference_contact_number_2} maxLength={10}
                    className={errors.reference_contact_number_2 ? "input-error" : ""}
                    onChange={(e) => field("reference_contact_number_2", formatDigits(e.target.value, 10))} />
                  {errors.reference_contact_number_2 && <span className="field-error">{errors.reference_contact_number_2}</span>}
                  <span className="field-hint">At least one reference contact number is required.</span>
                </div>
                <div className="form-section-title">
                  <span className="section-num">09</span>Photo & Joining
                </div>

                {renderFileField("driver_photo_url", "Upload Driver Photo")}

                <div className="form-field">
                  <label className="field-label">Date of Joining <span className="req">*</span></label>
                  <input type="date" value={form.date_of_joining}
                    min={MIN_DATE_STR}
                    max={TODAY_STR}
                    className={errors.date_of_joining ? "input-error" : ""}
                    onChange={(e) => field("date_of_joining", e.target.value)} />
                  {errors.date_of_joining && <span className="field-error">{errors.date_of_joining}</span>}
                </div>
                <div className="form-section-title">
                  <span className="section-num">10</span>Emergency Contact
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Emergency Contact Name"
                    value={form.emergency_contact_name}
                    onChange={(e) => field("emergency_contact_name", formatAlphaUpper(e.target.value))} />
                </div>

                <div className="form-field">
                  <input type="text" placeholder="Emergency Contact No * (starts with 6-9)"
                    value={form.emergency_contact_number} maxLength={10}
                    className={errors.emergency_contact_number ? "input-error" : ""}
                    onChange={(e) => field("emergency_contact_number", formatDigits(e.target.value, 10))} />
                  {errors.emergency_contact_number && <span className="field-error">{errors.emergency_contact_number}</span>}
                </div>
                <div className="form-section-title">
                  <span className="section-num">11</span>Character & Status
                </div>

                <div className="form-field">
                  <select value={form.character_nature}
                    onChange={(e) => field("character_nature", e.target.value)}
                    className={errors.character_nature ? "input-error" : ""}>
                    <option value="">Select Character Nature *</option>
                    {CHARACTER_NATURE_OPTIONS.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  {errors.character_nature && <span className="field-error">{errors.character_nature}</span>}
                </div>

                <div className="form-field">
                  <select value={form.status_id}
                    onChange={(e) => field("status_id", e.target.value)}
                    className={errors.status_id ? "input-error" : ""}>
                    <option value="">Select Status *</option>
                    {statuses.map((x) => (
                      <option key={x.id} value={String(x.id)}>{x.name}</option>
                    ))}
                  </select>
                  {errors.status_id && <span className="field-error">{errors.status_id}</span>}
                </div>

              </fieldset>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleBack}>
                  Back
                </button>
                {!readOnly && (
                  <button type="submit" className="submit-btn"
                    disabled={submitting || Object.values(uploadingFields).some(Boolean)}>
                    {submitting
                      ? (Object.values(uploadingFields).some(Boolean)
                        ? "Uploading files…"
                        : "Saving...")
                      : id ? "Update Driver" : "Create Driver"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
